import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { FormControlLabel, FormLabel, MenuItem, Select } from '@mui/material';
import { useState } from 'react';
import { useEffect } from 'react';
import { postApi, getApi } from 'views/services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import PropTypes from 'prop-types';

const RoomInvoiceDialog = (props) => {
  const navigate = useNavigate();
  const { open, handleClose, data } = props;
  console.log('data ==>', data);

  const [hotelData, setHotelData] = useState([]);
  const [haveGST, setHaveGST] = useState(false);
  const [gstAmount, setGstAmount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false); // Add loading state
  const hotel = JSON.parse(localStorage.getItem('hotelData'));

  // function for fetching hotel data from the db
  const fetchHotelData = async () => {
    try {
      const response = await getApi(`api/hotel/view/${hotel?.hotelId}`);
      setHotelData(response?.data);
    } catch (error) {
      console.log('Error fetching hotel data:', error);
      toast.error('Failed to load hotel data');
    }
  };

  useEffect(() => {
    if (open && hotel?.hotelId) {
      fetchHotelData();
    }
  }, [open, hotel?.hotelId]);

  const initialValues = {
    name: `${data?.customerDetails?.firstName || ''} ${data?.customerDetails?.lastName || ''}`.trim(),
    address: data?.customerDetails?.address || '',
    roomRent: data?.totalPayment || 0,
    advanceAmount: data?.advanceAmount || 0,
    pendingAmount: data?.totalAmount || 0,
    discount: 0,
    gstPercentage: hotelData?.roomgstpercentage || 0,
    haveGST: false,
    gstNumber: '',
    totalAmount: data?.totalAmount || 0,
    paymentMethod: 'cash'
  };

  const validationSchema = yup.object().shape({
    name: yup.string().required('Customer name is required'),
    address: yup.string().required('Address is required'),
    roomRent: yup.number().required('Room Rent is required').min(0, 'Room rent cannot be negative'),
    pendingAmount: yup.number().required('Pending amount is required').min(0, 'Pending amount cannot be negative'),
    advanceAmount: yup.number().required('Advance Amount is required').min(0, 'Advance amount cannot be negative'),
    discount: yup.number().min(0, 'Discount cannot be negative'),
    gstPercentage: yup.number().min(0, 'GST percentage cannot be negative'),
    gstNumber: yup.string().when('haveGST', {
      is: true,
      then: yup
        .string()
        .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Za-z]{1}[Z]{1}[0-9A-Za-z]{1}$/, 'Invalid GST number format')
        .required('GST number is required'),
      otherwise: yup.string()
    }),
    totalAmount: yup.number().required('Total amount is required').min(0, 'Total amount cannot be negative'),
    paymentMethod: yup.string().required('Payment method is required')
  });

  // function for generating Invoice Number
  const generateInvoiceNumber = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let randomPart = '';
    const charactersLength = characters.length;
    for (let i = 0; i < 4; i++) {
      randomPart += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return `INV-${year}-${month}-${day}-${randomPart}`;
  };

  // function for generating Invoice
  const generateRoomInvoice = async (values) => {
    try {
      setIsSubmitting(true);

      const invoiceData = {
        type: 'room',
        hotelId: hotel?.hotelId,
        reservationId: data?._id,
        invoiceNumber: generateInvoiceNumber(),
        customerPhoneNumber: data?.customerDetails?.phoneNumber,
        gstAmount: gstAmount,
        ...values
      };

      console.log('Submitting invoice data:', invoiceData);

      const response = await postApi('api/invoice/add', invoiceData);

      // Update hotel GST percentage if not set
      if (!hotelData?.roomgstpercentage && values.gstPercentage) {
        try {
          await postApi(`api/hotel/update/${hotel?.hotelId}`, {
            roomgstpercentage: values.gstPercentage
          });
        } catch (updateError) {
          console.warn('Failed to update hotel GST percentage:', updateError);
        }
      }

      toast.success('Invoice generated successfully');

      // Navigate to view page
      setTimeout(() => {
        navigate(`/roombill/view/${response.data._id}`);
      }, 1000);

      return true;
    } catch (error) {
      console.error('Invoice generation error:', error);
      toast.error(error.response?.data?.message || 'Cannot generate invoice');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      console.log("✅ Form submitted with values:", values);

      const success = await generateRoomInvoice(values);
      if (success) {
        handleClose();
        resetForm();
      }
    }
  });

  const handleGSTToggle = () => {
    const newHaveGST = !haveGST;
    setHaveGST(newHaveGST);
    formik.setFieldValue('haveGST', newHaveGST);

    // Reset discount when GST is enabled
    if (newHaveGST) {
      formik.setFieldValue('discount', 0);
    }
  };

  const calculateTotalAmount = () => {
    const roomRent = parseFloat(formik.values.roomRent) || 0;
    const discount = parseFloat(formik.values.discount) || 0;
    const gstPercentage = parseFloat(formik.values.gstPercentage) || 0;

    const totalAmountWithoutGst = roomRent - discount;
    const calculatedGstAmount = haveGST ? totalAmountWithoutGst * (gstPercentage / 100) : 0;

    setGstAmount(calculatedGstAmount);
    const totalAmount = totalAmountWithoutGst + calculatedGstAmount;

    formik.setFieldValue('totalAmount', Math.max(0, totalAmount).toFixed(2));
  };

  useEffect(() => {
    calculateTotalAmount();
  }, [
    formik.values.roomRent,
    formik.values.discount,
    formik.values.gstPercentage,
    haveGST
  ]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setHaveGST(false);
      setGstAmount(0);
      setIsSubmitting(false);
    }
  }, [open]);

  return (
    <div>
      <Dialog open={open} onClose={handleClose} aria-labelledby="scroll-dialog-title" aria-describedby="scroll-dialog-description">
        <DialogTitle
          id="scroll-dialog-title"
          style={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="h6">Invoice Information</Typography>
          <Typography>
            <Button onClick={handleClose} style={{ color: 'red' }} disabled={isSubmitting}>
              Cancel
            </Button>
          </Typography>
        </DialogTitle>
        <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
          <DialogContent dividers>
            <Grid container rowSpacing={3} columnSpacing={{ xs: 0, sm: 2 }}>
              {/* Customer Name and Address */}
              <Grid item xs={12} sm={6}>
                <FormLabel>Customer Name (Bill to)</FormLabel>
                <TextField
                  id="name"
                  name="name"
                  size="small"
                  type="text"
                  fullWidth
                  placeholder="Enter customer name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormLabel>Address</FormLabel>
                <TextField
                  id="address"
                  name="address"
                  size="small"
                  type="text"
                  fullWidth
                  placeholder="Enter customer address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                  disabled={isSubmitting}
                />
              </Grid>

              {/* Room Rent, Advance Amount, Pending Amount */}
              <Grid item xs={12} sm={6}>
                <FormLabel>Room Rent</FormLabel>
                <TextField
                  id="roomRent"
                  name="roomRent"
                  size="small"
                  type="number"
                  fullWidth
                  value={formik.values.roomRent}
                  InputProps={{ readOnly: true }}
                  error={formik.touched.roomRent && Boolean(formik.errors.roomRent)}
                  helperText={formik.touched.roomRent && formik.errors.roomRent}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormLabel>Advance Amount</FormLabel>
                <TextField
                  id="advanceAmount"
                  name="advanceAmount"
                  size="small"
                  type="number"
                  fullWidth
                  value={formik.values.advanceAmount}
                  InputProps={{ readOnly: true }}
                  error={formik.touched.advanceAmount && Boolean(formik.errors.advanceAmount)}
                  helperText={formik.touched.advanceAmount && formik.errors.advanceAmount}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormLabel>Pending Amount</FormLabel>
                <TextField
                  id="pendingAmount"
                  name="pendingAmount"
                  size="small"
                  type="number"
                  fullWidth
                  value={formik.values.pendingAmount}
                  InputProps={{ readOnly: true }}
                  error={formik.touched.pendingAmount && Boolean(formik.errors.pendingAmount)}
                  helperText={formik.touched.pendingAmount && formik.errors.pendingAmount}
                />
              </Grid>

              {/* Discount */}
              <Grid item xs={12} sm={6}>
                <FormLabel>Discount</FormLabel>
                <TextField
                  id="discount"
                  name="discount"
                  size="small"
                  type="number"
                  fullWidth
                  value={formik.values.discount}
                  onChange={(e) => {
                    let value = e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0);
                    const maxDiscount = formik.values.roomRent - 1;
                    value = Math.min(value, maxDiscount);
                    formik.setFieldValue('discount', value);
                  }}
                  error={formik.touched.discount && Boolean(formik.errors.discount)}
                  helperText={formik.touched.discount && formik.errors.discount}
                  disabled={formik.values.roomRent <= 0 || haveGST || isSubmitting}
                />
              </Grid>

              {/* Payment Method */}
              <Grid item xs={12} sm={6}>
                <FormLabel>Payment Method</FormLabel>
                <Select
                  id="paymentMethod"
                  name="paymentMethod"
                  size="small"
                  fullWidth
                  value={formik.values.paymentMethod}
                  onChange={formik.handleChange}
                  error={formik.touched.paymentMethod && Boolean(formik.errors.paymentMethod)}
                  disabled={isSubmitting}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="creditCard">Credit Card</MenuItem>
                  <MenuItem value="debitCard">Debit Card</MenuItem>
                  <MenuItem value="netBanking">Net Banking</MenuItem>
                  <MenuItem value="onlinePayment">Online Payment</MenuItem>
                </Select>
              </Grid>

              {/* GST Toggle */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={haveGST}
                      onChange={handleGSTToggle}
                      disabled={isSubmitting}
                    />
                  }
                  label="Have GST"
                />
              </Grid>

              {/* GST Number and Percentage */}
              {haveGST && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormLabel>GST Number</FormLabel>
                    <TextField
                      id="gstNumber"
                      name="gstNumber"
                      size="small"
                      type="text"
                      fullWidth
                      placeholder="Enter GST Number"
                      value={formik.values.gstNumber}
                      onChange={formik.handleChange}
                      error={formik.touched.gstNumber && Boolean(formik.errors.gstNumber)}
                      helperText={formik.touched.gstNumber && formik.errors.gstNumber}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  {!hotelData?.roomgstpercentage && (
                    <Grid item xs={12} sm={6}>
                      <FormLabel>GST Percentage</FormLabel>
                      <TextField
                        id="gstPercentage"
                        name="gstPercentage"
                        size="small"
                        type="number"
                        fullWidth
                        placeholder="In %"
                        value={formik.values.gstPercentage}
                        onChange={formik.handleChange}
                        error={formik.touched.gstPercentage && Boolean(formik.errors.gstPercentage)}
                        helperText={formik.touched.gstPercentage && formik.errors.gstPercentage}
                        disabled={isSubmitting}
                      />
                    </Grid>
                  )}
                </>
              )}

              {/* Total Amount */}
              <Grid item xs={12} sm={6}>
                <FormLabel>Total Amount</FormLabel>
                <TextField
                  id="totalAmount"
                  name="totalAmount"
                  size="small"
                  type="number"
                  fullWidth
                  value={formik.values.totalAmount}
                  InputProps={{ readOnly: true }}
                  error={formik.touched.totalAmount && Boolean(formik.errors.totalAmount)}
                  helperText={formik.touched.totalAmount && formik.errors.totalAmount}
                />
              </Grid>
            </Grid>
          </DialogContent>

          {/* Submit Button */}
          <DialogActions>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Generating...' : 'Generate Invoice'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

RoomInvoiceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired
};

export default RoomInvoiceDialog;